import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetProfessionalRulesetByIdQuery,
  useUpdateProfessionalRulesetMutation,
} from "store/queries/professionalRuleset";

import {
  IProfessionalRulesetFormData,
  INITIAL_PROFESSIONAL_FORM_DATA,
  ProfessionalRulesetForm,
} from "./components/ProfessionalRulesetForm";

export function EditProfessionalRulesetPage() {
  useDocumentTitle("Edit Professional Ruleset");
  const navigate = useNavigate();
  const { id } = useParams();
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetProfessionalRulesetByIdQuery(
    id ?? ""
  );
  const [updateMutation, { isLoading: isUpdating }] =
    useUpdateProfessionalRulesetMutation();
  const [formData, setFormData] = useState<IProfessionalRulesetFormData>(
    INITIAL_PROFESSIONAL_FORM_DATA
  );

  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      providerId: ruleset.providerId,
      payerId: ruleset.payerId,
      providerIds: [],
      payerIds: [],
      compoundBilateralAndMppr: ruleset.compoundBilateralAndMppr,
      endoApplies: ruleset.endoApplies,
      ncciApplies: ruleset.ncciApplies ?? true,
      mueApplies: ruleset.mueApplies ?? true,
      disabledEndoBaseCptCodes: ruleset.disabledEndoBaseCptCodes ?? [],
      primarySelectionMethod:
        ruleset.primarySelectionMethod ?? "ENDO_FAMILY_RVU",
    });
  }, [data]);

  const handleSubmit = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      await updateMutation({
        id,
        compoundBilateralAndMppr: formData.compoundBilateralAndMppr,
        endoApplies: formData.endoApplies,
        ncciApplies: formData.ncciApplies,
        mueApplies: formData.mueApplies,
        disabledEndoBaseCptCodes: formData.disabledEndoBaseCptCodes,
        primarySelectionMethod: formData.primarySelectionMethod,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Updated!",
        detail: "Professional ruleset updated successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to update professional ruleset",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <>
      <ProfessionalRulesetForm
        title="Edit Professional Ruleset"
        submitLabel="Save Changes"
        formData={formData}
        onChange={setFormData}
        onSubmit={handleSubmit}
        isSubmitting={isUpdating}
        scopeLocked
      />
      <RuleHistory histories={data?.data?.history} />
    </>
  );
}
